import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  TrackByFunction,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, takeUntil, of, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { SlotService } from '../../../core/services/slot.service';
import {
  WeekSlots,
  DaySlot,
  TimeSlot,
} from '../../../core/interfaces/slot.interface';
import { FormGroup } from '@angular/forms';
import { ReferentielService } from '../../../core/services/referentiel.service';
import {
  AlertCircleIcon,
  LucideAngularModule,
  ChevronLeftIcon,
  LoaderIcon,
  ChevronRightIcon,
} from 'lucide-angular';
import {
  Agenda,
  AppointmentAvailability,
  AppointmentService,
} from '../../../core/services';
import { AuthService } from '../../../core/services/auth.service';
import { ModalService } from '../../../core/services/modal.service';
import { UserService } from '../../../core/services/user.service';
import { LoginRequiredModalComponent } from '../modal/login-required-modal/login-required-modal.component';
import { AppointmentModalComponent } from '../modal/appointment-modal/appointment-modal.component';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { selectAllSubAccounts } from '../../../store/sub-accounts/sub-accounts.selectors';

interface Timeslot {
  date: string;
  agendaId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  startDateFull: string;
  endDateFull: string;
  isAvailable: boolean;
}

interface NextDay {
  date: Date;
  dayName: string;
  month: string;
  day: number;
  slots: Timeslot[];
  id: string; // Unique identifier for trackBy
}

@Component({
  selector: 'app-doctor-slots',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './doctor-slots.component.html',
  styleUrls: ['./doctor-slots.component.scss'],
})
export class DoctorSlotsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() doctorId?: string;
  @Input() structureId?: string;
  @Input() doctorName?: string;
  @Input() structureName?: string;
  @Input() specialisations: string[] = [];
  @Input() reasons: any[] = [];

  readonly ChevronLeftIcon = ChevronLeftIcon;
  readonly ChevronRightIcon = ChevronRightIcon;
  readonly AlertCircleIcon = AlertCircleIcon;
  readonly LoaderIcon = LoaderIcon;

  agenda?: Agenda;

  emptySlots = false;
  loading = false;

  page = 1;
  limit = 99;

  private destroy$ = new Subject<void>();

  nextSixDays: NextDay[] = [];

  private daysOfWeek = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ];
  private months = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  private currentStartDate: Date = new Date();

  constructor(
    private readonly appointmentService: AppointmentService,
    private authService: AuthService,
    private modalService: ModalService,
    private userService: UserService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.currentStartDate = this.getStartOfDay(new Date());
    this.fillNextSixDays(this.currentStartDate);
    this.fetchSlots();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sid = changes['structureId'];
    const did = changes['doctorId'];
    if (!sid && !did) return;
    const isInitial =
      (sid?.firstChange && !did) ||
      (did?.firstChange && !sid) ||
      (sid?.firstChange && did?.firstChange);
    if (isInitial) return;
    if (this.structureId && this.doctorId) {
      this.agenda = undefined;
      this.emptySlots = false;
      this.currentStartDate = this.getStartOfDay(new Date());
      this.fillNextSixDays(this.currentStartDate);
      this.fetchSlots();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Format a Date object as "YYYY-MM-DD"
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  fetchSlots(): void {
    this.loading = true;
    if (!this.nextSixDays.length) {
      return;
    }
    const startDate = this.formatDate(this.nextSixDays[0].date);
    const endDate = this.formatDate(
      this.nextSixDays[this.nextSixDays.length - 1].date
    );
    this.appointmentService
      .getAppointmentsAvailabilities(
        { limit: this.limit, page: this.page },
        this.structureId,
        this.doctorId,
        startDate,
        endDate
      )
      .subscribe({
        next: (response) => {
          this.agenda = response.agenda;
          const slots: Timeslot[] = response.avaibilities;
          // Réinitialiser les slots pour chaque jour
          for (const day of this.nextSixDays) {
            day.slots = [];
          }
          
          // Pour chaque slot, trouver le jour correspondant et l'ajouter
          slots.forEach((slot) => {
            // slot.date est supposé être au format "YYYY-MM-DD"
            const slotDate = slot.date;
            for (const day of this.nextSixDays) {
              const dayDateStr = this.formatDate(day.date);
              if (dayDateStr === slotDate) {
                day.slots.push(slot);
                break;
              }
            }
          });
          this.emptySlots = slots.length === 0;
        },
        error: () => {
          this.emptySlots = true;
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  private getStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private fillNextSixDays(startDate: Date): void {
    this.nextSixDays = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dayKey = this.formatDate(date) + '-' + i; // ensure uniqueness even for same date
      this.nextSixDays.push({
        date: date,
        dayName: this.daysOfWeek[date.getDay()],
        month: this.months[date.getMonth()],
        day: date.getDate(),
        slots: [],
        id: dayKey, // unique key
      });
    }
  }

  previousWeek(): void {
    // Recule de 6 jours à partir du premier jour actuellement affiché
    const prevStart = new Date(this.currentStartDate);
    prevStart.setDate(this.currentStartDate.getDate() - 6);
    this.currentStartDate = this.getStartOfDay(prevStart);
    this.fillNextSixDays(this.currentStartDate);
    this.fetchSlots();
  }

  nextWeek(): void {
    // Avance de 6 jours à partir du premier jour actuellement affiché
    const nextStart = new Date(this.currentStartDate);
    nextStart.setDate(this.currentStartDate.getDate() + 6);
    this.currentStartDate = this.getStartOfDay(nextStart);
    this.fillNextSixDays(this.currentStartDate);
    this.fetchSlots();
  }

  /**
   * Gère le clic sur un slot
   */
  onSlotClick(slot: Timeslot): void {
    if (!this.authService.isAuthenticated()) {
      // Si non connecté, ouvrir le modal de connexion requise
      this.openLoginRequiredModal();
    } else {
      // Si connecté, ouvrir le modal de prise de rendez-vous
      this.openAppointmentModal(slot);
    }
  }

  /**
   * Ouvre le modal de connexion requise
   */
  private openLoginRequiredModal(): void {
    this.modalService.open({
      component: LoginRequiredModalComponent,
      width: '500px',
      closable: true,
    });
  }

  /**
   * Ouvre le modal de prise de rendez-vous
   */
  private openAppointmentModal(slot: Timeslot): void {
    // Récupérer l'utilisateur courant et les sous-comptes
    const currentUser$ = this.store.select(selectCurrentUser);
    const subAccounts$ = this.store.select(selectAllSubAccounts);

    // Combiner les observables pour récupérer toutes les données
    combineLatest([currentUser$, subAccounts$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([currentUser, subAccounts]) => {
        this.modalService.open({
          component: AppointmentModalComponent,
          width: '500px',
          closable: true,
          data: {
            slot: slot,
            doctorId: this.doctorId,
            structureId: this.structureId,
            specialisations:this.specialisations,
            doctorName: this.doctorName,
            structureName: this.structureName,
            doctorSpecialisations: this.specialisations,
            doctorReasonAppts: this.reasons,
            currentUser: currentUser,
            subAccounts: subAccounts || [],
            agenda: this.agenda,
          },
        });
      });
  }

  /**
   * TrackBy function to uniquely identify days for ngFor to prevent duplicated keys.
   */
  trackByDay: TrackByFunction<NextDay> = (index, item) => item.id;
}
